import { ArrowRight, Check } from "lucide-react";
import { highlightPricingTextFeatures } from "@/components/pricing/pricing_text_highlight";
import { Button } from "@/components/ui/button";
import {
	type CategoryIconVariant,
	WATERMARK_LINES,
} from "./pricing_card_watermark_paths";

export function PricingCardWatermark({
	variant,
}: {
	variant: CategoryIconVariant;
}) {
	const lineBoxes = WATERMARK_LINES[variant];

	return (
		<div
			aria-hidden="true"
			className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-3xl text-zinc-950 opacity-[0.08]"
			data-pricing-card-watermark="true"
		>
			<div
				className="absolute right-0 top-[44%] flex h-[78%] w-24 -translate-y-1/2 items-center justify-center overflow-visible"
				data-pricing-card-watermark-lines="true"
			>
				<div
					className="flex w-[240px] shrink-0 rotate-90 flex-col items-center justify-center gap-0"
					data-pricing-card-watermark-rotator="true"
				>
					{lineBoxes.map((line) => (
						<svg
							aria-hidden="true"
							className={`${line.heightClass} max-w-none overflow-hidden ${line.widthClass}`}
							data-pricing-card-watermark-line="true"
							focusable="false"
							key={line.viewBox}
							preserveAspectRatio="xMidYMid meet"
							viewBox={line.viewBox}
						>
							{line.paths.map((path) => (
								<path
									d={path.d}
									fill="currentColor"
									key={`${path.x}-${path.y}-${path.d.length}-${path.d.slice(0, 16)}`}
									stroke="currentColor"
									strokeLinejoin="round"
									strokeWidth={2}
									transform={`translate(${path.x} ${path.y})`}
								/>
							))}
						</svg>
					))}
				</div>
			</div>
		</div>
	);
}

interface PricingCardProps {
	name: string;
	price: number;
	currency: string;
	features: string[];
	popular: boolean;
	onGetStarted: () => void;
	index: number;
	size?: "default" | "featured";
	description?: string;
	accentColor?: string;
	categoryIcon?: CategoryIconVariant;
}

export function PricingCardArtistic({
	name,
	price,
	currency,
	features,
	popular,
	onGetStarted,
	size = "default",
	description,
	categoryIcon,
}: PricingCardProps) {
	const isINR = currency === "inr";
	const currencySymbol = isINR ? "₹" : "$";
	const isFeatured = size === "featured";
	const panelPadding = isFeatured ? "p-10" : "p-8";
	const titleSize = isFeatured ? "text-xl" : "text-lg";
	const priceTextSize = isFeatured ? "text-5xl" : "text-4xl";
	const descriptionSize = isFeatured ? "text-sm" : "text-xs";
	const featureSpacing = isFeatured ? "space-y-5" : "space-y-4";
	const featureTextSize = isFeatured ? "text-base" : "text-sm";
	const ctaSize = isFeatured ? "h-14" : "h-12";

	return (
		<div
			className={`
            group relative isolate h-full bg-white rounded-3xl ${panelPadding}
            border ${popular ? "border-red-200 shadow-red-950/10" : "border-zinc-100"} shadow-xl flex flex-col overflow-visible
        `}
		>
			{categoryIcon && <PricingCardWatermark variant={categoryIcon} />}

			{/* Popular Badge - Reverse leaf style */}
			{popular && (
				<div
					className="absolute z-20"
					style={{
						top: "1.5px",
						right: "-0.5px",
					}}
				>
					<span className="bg-red-600 text-white text-xs font-bold px-6 py-2 rounded-tr-xl rounded-bl-xl shadow-lg">
						Best Value
					</span>
				</div>
			)}

			{/* Header */}
			<div className="relative z-10 mb-8">
				<h3
					className={`font-syne font-bold uppercase text-zinc-400 mb-2 tracking-wide ${titleSize}`}
				>
					{name}
				</h3>
				<div className="flex items-baseline gap-1">
					<span
						className={`font-jost font-bold ${priceTextSize} text-zinc-900 tracking-tight`}
					>
						{currencySymbol}
						{price.toLocaleString()}
					</span>
				</div>
				{description && (
					<p
						className={`font-inter font-medium text-zinc-400 mt-2 uppercase tracking-wider ${descriptionSize}`}
					>
						{description}
					</p>
				)}
			</div>

			{/* Features */}
			<div className={`relative z-10 flex-1 mb-8 ${featureSpacing}`}>
				{(features ?? []).map((feature) => (
					<div key={feature} className="flex items-start gap-3 group/item">
						<div
							className={`mt-1 w-4 h-4 rounded-full bg-zinc-50 flex items-center justify-center`}
						>
							<Check className="w-3 h-3 text-red-600" />
						</div>
						<span
							className={`font-inter ${featureTextSize} text-zinc-600 leading-tight group-hover/item:text-zinc-900 transition-colors`}
						>
							{highlightPricingTextFeatures(feature)}
						</span>
					</div>
				))}
			</div>

			{/* CTA */}
			<Button
				type="button"
				onClick={onGetStarted}
				data-analytics-event="select_item"
				data-analytics-label={`plan_${name}`}
				className={`relative z-10 w-full ${ctaSize} rounded-xl font-syne font-bold uppercase tracking-wide transition-all duration-300
                    ${
											popular
												? "bg-red-600 text-white hover:bg-red-700"
												: "bg-zinc-900 text-white hover:bg-red-600"
										}
                `}
			>
				<span>Select Plan</span>
				<ArrowRight className="w-4 h-4 ml-2" />
			</Button>
		</div>
	);
}
